/**
 * Token Refresh Service
 * Schedules proactive refresh 1-2 min before expiry and expiry warning 30 sec before.
 * Single source of truth for auth timing logic.
 */

import axios from "axios";
import { store } from "../../redux/store";
import { setTokens } from "../../redux/slices/AuthSlice";
import { performLogout } from "../performLogout";
import { getAccessTokenExpiryMs } from "../utils/jwt";
import { refreshMutex } from "../utils/refreshMutex";
import { updateSocketToken } from "../../services/socket";
import { serverURL } from "../../Apis/auth";
import {
  clearAllSessionTimers,
  getWarningCountdownId,
  setRefreshTimerId,
  setWarningCountdownId,
  setWarningTimerId,
} from "../utils/sessionTimers";

const REFRESH_BEFORE_EXPIRY_MS = 90 * 1000; // 1.5 min before expiry
const EXPIRY_WARNING_BEFORE_MS = 30 * 1000; // 30 sec before expiry
const WARNING_COUNTDOWN_MS = 30 * 1000; // 30 sec for user to click "Stay Logged In"
const EXTEND_SESSION_THROTTLE_MS = 5 * 60 * 1000; // at most one heartbeat per 5 min on activity

const headers: Record<string, string> = {
  Accept: "application/json",
  "Content-Type": "application/json; charset=utf-8",
};

let onExpiryWarningCallback: (() => void) | null = null;
let lastExtendOnActivityAt = 0;

export function setExpiryWarningCallback(cb: (() => void) | null): void {
  onExpiryWarningCallback = cb;
}

export function clearAllTimers(): void {
  clearAllSessionTimers();
  lastExtendOnActivityAt = 0;
}

function isUnauthorizedError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  return (error as { response?: { status?: number } }).response?.status === 401;
}

async function refreshWithLatestToken(): Promise<{
  accessToken: string;
  refreshToken: string;
  accessExpiresInSec?: number;
  refreshExpiresAt?: string;
  jti?: string;
}> {
  return refreshMutex.run(() => {
    const rt = store.getState().auth.refreshToken;
    if (!rt) {
      throw new Error("No refresh token");
    }
    return callRefreshApi(rt);
  });
}

/**
 * Tell the server the user is still active. This updates lastActiveAt.
 * Refresh does not, when STRICT_IDLE_ON_REFRESH is on.
 */
export async function callHeartbeatApi(accessToken: string): Promise<void> {
  await axios.get(`${serverURL}/auth/heartbeat`, {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

async function touchSessionOrRecover(): Promise<void> {
  const accessToken = store.getState().auth.accessToken;
  if (!accessToken) return;
  try {
    await callHeartbeatApi(accessToken);
  } catch (error) {
    if (!isUnauthorizedError(error)) {
      return;
    }
    try {
      const tokens = await refreshWithLatestToken();
      handleRefreshSuccess(tokens);
      await callHeartbeatApi(tokens.accessToken);
    } catch {
      handleRefreshFailure();
    }
  }
}

/**
 * Call refresh API directly (plain axios, no interceptor).
 */
export async function callRefreshApi(refreshToken: string): Promise<{
  accessToken: string;
  refreshToken: string;
  accessExpiresInSec?: number;
  refreshExpiresAt?: string;
  jti?: string;
}> {
  const response = await axios.post(
    `${serverURL}/auth/refresh`,
    { refreshToken },
    { headers }
  );

  const data = response.data;
  if (!data?.accessToken || !data?.refreshToken) {
    throw new Error("Invalid refresh response");
  }

  return {
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    accessExpiresInSec: data.accessExpiresInSec,
    refreshExpiresAt: data.refreshExpiresAt,
    jti: data.jti,
  };
}

function handleRefreshSuccess(tokens: {
  accessToken: string;
  refreshToken: string;
  accessExpiresInSec?: number;
  refreshExpiresAt?: string;
  jti?: string;
}): void {
  store.dispatch(setTokens(tokens));
  updateSocketToken(tokens.accessToken);
  scheduleFromTokens(
    tokens.accessToken,
    tokens.refreshToken,
    tokens.accessExpiresInSec
  );
}

function handleRefreshFailure(): void {
  clearAllTimers();
  void performLogout({
    callLogoutApi: false,
    showToast: true,
    toastMessage: "Session expired. Please login again.",
  }).then(() => {
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  });
}

/**
 * Schedule proactive refresh and expiry warning from current tokens.
 */
export function scheduleFromTokens(
  accessToken: string,
  refreshToken: string,
  accessExpiresInSec?: number
): void {
  clearAllTimers();

  const expiryMs = getAccessTokenExpiryMs(accessToken, accessExpiresInSec);
  if (!expiryMs) return;

  const now = Date.now();
  const timeUntilExpiry = expiryMs - now;

  if (timeUntilExpiry <= 0) {
    handleRefreshFailure();
    return;
  }

  // Schedule proactive refresh 1.5 min before expiry
  const refreshIn = Math.max(0, timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS);
  setRefreshTimerId(
    setTimeout(async () => {
      setRefreshTimerId(null);
      const state = store.getState();
      const rt = state.auth.refreshToken;
      if (!rt) {
        handleRefreshFailure();
        return;
      }
      try {
        const tokens = await refreshWithLatestToken();
        handleRefreshSuccess(tokens);
      } catch {
        handleRefreshFailure();
      }
    }, refreshIn)
  );

  // Schedule expiry warning 30 sec before
  const warningIn = Math.max(0, timeUntilExpiry - EXPIRY_WARNING_BEFORE_MS);
  setWarningTimerId(
    setTimeout(() => {
      setWarningTimerId(null);
      if (onExpiryWarningCallback) {
        onExpiryWarningCallback();
      }
      // Start 30-sec countdown; if user doesn't click "Stay Logged In", logout
      setWarningCountdownId(
        setTimeout(() => {
          setWarningCountdownId(null);
          handleRefreshFailure();
        }, WARNING_COUNTDOWN_MS)
      );
    }, warningIn)
  );
}

/**
 * User clicked "Stay Logged In" - refresh immediately and reschedule.
 */
export async function handleStayLoggedIn(): Promise<boolean> {
  const countdownId = getWarningCountdownId();
  if (countdownId) {
    clearTimeout(countdownId);
    setWarningCountdownId(null);
  }
  if (!store.getState().auth.refreshToken) return false;
  try {
    const tokens = await refreshWithLatestToken();
    handleRefreshSuccess(tokens);
    try {
      await callHeartbeatApi(tokens.accessToken);
    } catch {
      // Tokens already rotated; idle touch is best-effort.
    }
    return true;
  } catch {
    handleRefreshFailure();
    return false;
  }
}

export type ExtendSessionOnActivityOptions = {
  /** Skip the 5-minute throttle (open call form, search modal, live call). */
  force?: boolean;
};

/**
 * Extend session on user activity (click, keydown, input, pointer, paste, or tab focus).
 * Throttled to at most one heartbeat per 5 minutes unless `force` is set.
 * Heartbeat updates backend lastActiveAt; /auth/refresh does not when strict idle is on.
 */
export function extendSessionOnActivity(
  options?: ExtendSessionOnActivityOptions
): Promise<void> {
  const countdownId = getWarningCountdownId();
  const now = Date.now();
  if (
    !options?.force &&
    !countdownId &&
    now - lastExtendOnActivityAt < EXTEND_SESSION_THROTTLE_MS
  ) {
    return Promise.resolve();
  }
  const state = store.getState();
  if (!state.auth.refreshToken || !state.auth.accessToken) {
    return Promise.resolve();
  }

  lastExtendOnActivityAt = now;
  if (countdownId) {
    return handleStayLoggedIn().then(() => undefined);
  }
  return touchSessionOrRecover();
}
